import React, { useEffect, useState } from "react";
import { useIsClient } from "@/hooks/useIsClient";
import { CloseIcon, SlideOver } from "@picklebet/ui";
import { getGeoInfo } from "@/features/auth/services/session";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { isWebView } from "@/utilities/display";
import { useNavTree } from "../hooks/useNavTree";
import { useShellStore } from "../stores/shellStore";
import type { NavLocation } from "../types";
import { Navbar } from "./Navbar";
import { NavTree } from "./NavTree";

import * as styles from "./ShellNavDrawer.module.scss";

const useIsUseAppVisible = () => {
  const isClient = useIsClient();
  const [geo, setGeo] = useState<{ twoLetterCountryCode?: string } | null>(
    null,
  );
  const isLoggedIn = useAuthStore((state) => state.status === "authenticated");
  const { profile } = useProfile();

  useEffect(() => {
    if (isLoggedIn) return;

    const loadGeo = async () => {
      const response = await getGeoInfo();
      if (response.ok) setGeo(response.data);
    };
    loadGeo();
  }, [isLoggedIn]);

  const isAuIp = geo?.twoLetterCountryCode === "AU";
  const isAuResident = profile?.country === "AU";

  return isClient && !isWebView() && (isLoggedIn ? isAuResident : isAuIp);
};

const DrawerNav = ({ location }: { location: NavLocation }) => {
  const tree = useNavTree();

  return (
    <Navbar.Root aria-label="Main">
      <NavTree tree={tree} location={location} />
    </Navbar.Root>
  );
};

export const ShellNavDrawer = ({ location }: { location: NavLocation }) => {
  const menuOpen = useShellStore((state) => state.menuOpen);
  const setMenuOpen = useShellStore((state) => state.setMenuOpen);
  const isUseAppVisible = useIsUseAppVisible();

  return (
    <SlideOver.Root
      side="left"
      open={menuOpen}
      onOpenChange={setMenuOpen}
      isElastic={false}
      isDirectionLocked
    >
      <SlideOver.Portal>
        <SlideOver.Backdrop className={styles.backdrop} />
        <SlideOver.Panel className={styles.panel}>
          <SlideOver.Title visuallyHidden>Menu</SlideOver.Title>
          <header className={styles.header}>
            {isUseAppVisible && (
              <a
                className={styles.useApp}
                href="https://picklebet.onelink.me/8Ie4/309baad5"
              >
                Use App
              </a>
            )}
            <SlideOver.Close>
              <button
                type="button"
                className={styles.close}
                aria-label="Close menu"
              >
                <CloseIcon />
              </button>
            </SlideOver.Close>
          </header>
          <div className={styles.body}>
            <DrawerNav location={location} />
          </div>
        </SlideOver.Panel>
      </SlideOver.Portal>
    </SlideOver.Root>
  );
};
